import React from 'react';
import { css } from 'styled-components';
import { Text } from '@lmn/design-system';
import { EvoRoomAvailabilityAlertLabel } from '@/app/components/hotel-detail/evo/rooms/EvoRoomAvailabilityAlertLabel';
import { EvoRoomCancellationTypeSelection } from '@/app/components/hotel-detail/evo/rooms/EvoRoomCancellationTypeSelection';
import RoomMainAmenities, { RoomMainAmenity } from '@/app/components/hotel-detail/rooms/RoomMainAmenities';
import { RoomSimpleAmenities } from '@/app/components/hotel-detail/rooms/RoomSimpleAmenities';
import { RoomBedOptions } from '@/app/components/hotel-detail/rooms/RoomBedOptions';
import { RoomsIncludedInPrice } from '@/app/components/hotel-detail/rooms/RoomPanelCommon';
import { getMainAmenities } from '@/app/components/hotel-detail/rooms/roomCommon';
import ImageSlider from '@/app/components/hotel-detail/ImageSlider';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { useTranslation } from '@/app/components/translations/Translation';

type EvoRoomPanelModalContentDesktopProps = {
  room: RoomType;
  selectedMealPlan?: MealPlanType;
  onSelectMealPlan?: (selectedMealPlan: MealPlanType) => void;
} & React.HTMLAttributes<HTMLElement>;

function getRoomGeneralDescription(descriptions: readonly { type: string; text: string }[]): string | undefined {
  const text = descriptions.find((description) => description?.type === 'GENERAL')?.text?.trim();
  return text && text.length > 1 ? text : undefined;
}

// Desktop body of the EVO room-details modal (Figma 979-40540 / 979-40645): a full-width gallery with the
// image counter centered, the room info at Body size, and the bordered cancellation-type selector. The
// "Continue" CTA lives in the modal footer (RoomPanelModal). Mirrors EvoRoomPanelModalContentMobile, but
// kept separate because desktop uses larger type (Title3 name, Body content) and a different gallery layout.
export function EvoRoomPanelModalContentDesktop(props: EvoRoomPanelModalContentDesktopProps) {
  const { room, selectedMealPlan, onSelectMealPlan, ...rest } = props;
  const { t } = useTranslation();

  const roomsLeft = room.configurations?.[0]?.roomsLeft ?? 0;
  const mainAmenities = getMainAmenities(t, room);
  const simpleAmenities = room.amenities.filter((amenity) => amenity.type === 'GENERAL');
  const description = getRoomGeneralDescription(room.descriptions);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
        /* The modal body is edge to edge for this modal, so the desktop insets live here instead:
           flush under the sticky title bar like every other EVO modal body, 24px at the sides, and
           24px below the last block — the same bottom space the other modals get (Figma 2428-57266,
           where the inline price/CTA slot is hidden). A room that came back with no images opens on
           its name rather than the gallery, and sits flush the same way the other bodies do. */
        padding: 0 24px 24px;
      `}
      {...rest}
    >
      {room.images.length ? (
        <ImageSlider
          images={room.images}
          imageAltName={room.name}
          showCounterIcon={false}
          counterPosition={`
            bottom: 16px;
            left: 50%;
            transform: translateX(-50%);
          `}
          css={css`
            height: 304px;
            border-radius: 16px;
            overflow: hidden;

            & .slider-overlay-arrow-left,
            & .slider-overlay-arrow-right {
              display: flex;

              background: rgba(255, 255, 255, 1);
              border-radius: 99999px;
              width: 40px;
              height: 40px;
              align-items: center;

              &:hover {
                background: rgba(255, 255, 255, 0.9);
              }

              & > [data-library-icon] {
                color: ${(p) => p.theme.commons.colors.gray800};
                width: 20px;
                height: 20px;
              }
            }

            & .slider-overlay-arrow-left {
              left: 16px;
            }

            & .slider-overlay-arrow-right {
              right: 16px;
            }
          `}
        />
      ) : null}

      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 16px;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 4px 8px;
            `}
          >
            {room.roomsIncludedInPrice > 1 && <RoomsIncludedInPrice roomsIncludedInPrice={room.roomsIncludedInPrice} />}
            <Text variant="Title3">{room.name}</Text>
          </div>
          <EvoRoomAvailabilityAlertLabel roomsLeft={roomsLeft} />
        </div>

        {room.bedOptions?.length ? <RoomBedOptions bedOptions={room.bedOptions} iconSize={20} fontSize="Body" /> : null}

        {mainAmenities.length > 0 && (
          <RoomMainAmenities
            amenities={mainAmenities}
            iconColor="iconPrimary"
            iconSize={20}
            textVariant="Body"
            css={css`
              display: flex;
              flex-wrap: wrap;
              width: 100%;
              column-gap: 20px;
              row-gap: 8px;

              ${RoomMainAmenity} {
                align-items: center;
              }
            `}
          />
        )}

        {description && <Text variant="Body">{description}</Text>}

        {simpleAmenities.length > 0 && (
          <RoomSimpleAmenities
            amenities={simpleAmenities}
            textVariant="Body"
            css={css`
              @media ${customCss.breakpoints.sm} {
                column-count: 1;
              }
            `}
          />
        )}

        {/* No wrapper of its own: the column's 16px gap already separates the selector from the block
            above it, same as the mobile body. */}
        {selectedMealPlan && onSelectMealPlan && (
          <EvoRoomCancellationTypeSelection
            room={room}
            selectedMealPlan={selectedMealPlan}
            onSelectMealPlan={onSelectMealPlan}
            bordered={true}
          />
        )}
      </div>
    </div>
  );
}
